// Currency mapping based on country codes
// Same numeric value, different currency symbol (no conversion)

const CURRENCY_MAP = {
  // Americas
  '+1': { symbol: '$', code: 'USD', name: 'US Dollar' },
  '+55': { symbol: 'R$', code: 'BRL', name: 'Brazilian Real' },
  '+52': { symbol: '$', code: 'MXN', name: 'Mexican Peso' },
  '+54': { symbol: '$', code: 'ARS', name: 'Argentine Peso' },
  '+56': { symbol: '$', code: 'CLP', name: 'Chilean Peso' },
  '+57': { symbol: '$', code: 'COP', name: 'Colombian Peso' },
  '+58': { symbol: 'Bs', code: 'VES', name: 'Venezuelan Bolívar' },
  
  // Europe
  '+44': { symbol: '£', code: 'GBP', name: 'British Pound' },
  '+49': { symbol: '€', code: 'EUR', name: 'Euro' },
  '+33': { symbol: '€', code: 'EUR', name: 'Euro' },
  '+39': { symbol: '€', code: 'EUR', name: 'Euro' },
  '+34': { symbol: '€', code: 'EUR', name: 'Euro' },
  '+351': { symbol: '€', code: 'EUR', name: 'Euro' },
  '+31': { symbol: '€', code: 'EUR', name: 'Euro' },
  '+32': { symbol: '€', code: 'EUR', name: 'Euro' },
  '+43': { symbol: '€', code: 'EUR', name: 'Euro' },
  '+30': { symbol: '€', code: 'EUR', name: 'Euro' },
  '+353': { symbol: '€', code: 'EUR', name: 'Euro' },
  '+358': { symbol: '€', code: 'EUR', name: 'Euro' },
  '+41': { symbol: 'CHF', code: 'CHF', name: 'Swiss Franc' },
  '+46': { symbol: 'kr', code: 'SEK', name: 'Swedish Krona' },
  '+47': { symbol: 'kr', code: 'NOK', name: 'Norwegian Krone' },
  '+45': { symbol: 'kr', code: 'DKK', name: 'Danish Krone' },
  '+48': { symbol: 'zł', code: 'PLN', name: 'Polish Zloty' },
  '+7': { symbol: '₽', code: 'RUB', name: 'Russian Ruble' },
  '+90': { symbol: '₺', code: 'TRY', name: 'Turkish Lira' },
  
  // Asia
  '+81': { symbol: '¥', code: 'JPY', name: 'Japanese Yen' },
  '+86': { symbol: '¥', code: 'CNY', name: 'Chinese Yuan' },
  '+91': { symbol: '₹', code: 'INR', name: 'Indian Rupee' },
  '+82': { symbol: '₩', code: 'KRW', name: 'South Korean Won' },
  '+65': { symbol: 'S$', code: 'SGD', name: 'Singapore Dollar' },
  '+60': { symbol: 'RM', code: 'MYR', name: 'Malaysian Ringgit' },
  '+63': { symbol: '₱', code: 'PHP', name: 'Philippine Peso' },
  '+66': { symbol: '฿', code: 'THB', name: 'Thai Baht' },
  '+84': { symbol: '₫', code: 'VND', name: 'Vietnamese Dong' },
  '+62': { symbol: 'Rp', code: 'IDR', name: 'Indonesian Rupiah' },
  '+971': { symbol: 'د.إ', code: 'AED', name: 'UAE Dirham' },
  '+966': { symbol: '﷼', code: 'SAR', name: 'Saudi Riyal' },
  '+972': { symbol: '₪', code: 'ILS', name: 'Israeli Shekel' },
  
  // Oceania
  '+61': { symbol: 'A$', code: 'AUD', name: 'Australian Dollar' },
  '+64': { symbol: 'NZ$', code: 'NZD', name: 'New Zealand Dollar' },
  
  // Africa
  '+27': { symbol: 'R', code: 'ZAR', name: 'South African Rand' },
  '+20': { symbol: 'E£', code: 'EGP', name: 'Egyptian Pound' },
  '+234': { symbol: '₦', code: 'NGN', name: 'Nigerian Naira' },
  '+254': { symbol: 'KSh', code: 'KES', name: 'Kenyan Shilling' },
};

// Default currency (USD)
const DEFAULT_CURRENCY = { symbol: '$', code: 'USD', name: 'US Dollar' };

/**
 * Get currency info based on country code
 * @param {string} countryCode - Phone country code like '+55' or '+1'
 * @returns {object} Currency info with symbol, code, name
 */
export const getCurrencyByCountryCode = (countryCode) => {
  if (!countryCode) return DEFAULT_CURRENCY;
  
  // Normalize country code (remove spaces, ensure starts with +)
  const normalized = countryCode.replace(/\s/g, '');
  const code = normalized.startsWith('+') ? normalized : `+${normalized}`;
  
  return CURRENCY_MAP[code] || DEFAULT_CURRENCY;
};

/**
 * Format price with local currency symbol (same numeric value)
 * @param {number} amount - The price amount (same across all currencies)
 * @param {string} countryCode - Phone country code
 * @returns {string} Formatted price string
 */
export const formatPrice = (amount, countryCode) => {
  const currency = getCurrencyByCountryCode(countryCode);
  
  // Format number with locale-appropriate separators
  const formattedNumber = amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return `${currency.symbol}${formattedNumber}`;
};

/**
 * Get currency symbol only
 * @param {string} countryCode - Phone country code
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (countryCode) => {
  return getCurrencyByCountryCode(countryCode).symbol;
};

/**
 * Detect user's country code from browser or stored preference
 * @returns {string} Country code with + prefix
 */
export const detectUserCountryCode = () => {
  // First check localStorage for user's selected country code
  const storedCode = localStorage.getItem('user_country_code');
  if (storedCode) return storedCode;
  
  // Try to detect from browser language
  const browserLang = navigator.language || navigator.userLanguage;
  
  const langToCountry = {
    'pt-BR': '+55',
    'pt': '+351',
    'es-MX': '+52',
    'es-AR': '+54',
    'es-ES': '+34',
    'es': '+34',
    'en-GB': '+44',
    'en-AU': '+61',
    'en-US': '+1',
    'en': '+1',
    'de': '+49',
    'fr': '+33',
    'it': '+39',
    'ja': '+81',
    'zh-CN': '+86',
    'zh': '+86',
    'ko': '+82',
    'ru': '+7',
    'ar': '+971',
  };
  
  return langToCountry[browserLang] || langToCountry[browserLang.split('-')[0]] || '+1';
};

export default {
  getCurrencyByCountryCode,
  formatPrice,
  getCurrencySymbol,
  detectUserCountryCode,
  CURRENCY_MAP
};
